import { useState } from "react";
import { Card, Col, Row, Form, FormCheck, Nav, Tab } from "react-bootstrap";
import { TextField } from "@components/FormsV2";
import { capitalizeFirstLetter, snakeToText } from "@utils/helperFunctions";
import { User, Privilege } from "./types";
import { MultiSelectAutocomplete } from "@components/FormsV2/MultiSelectAutoComplete";
import { V1AccountResource, V1RoleResource, V1TeamResource } from "@generated";
import { User as Editor } from "app/javascript/src/types";
import { useListPrivileges } from "./api";
import DataTable from "@components/DataTable";
import { ColumnDef } from "@tanstack/react-table";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import { Select2V2 } from "@components/FormsV2";
import useFlags from "@common/hooks/useFlags";
import usePrivilegeCheck from "@common/hooks/usePrivilegeCheck";
import ClientCredentials from "./ClientCredentials";
import VersionHistory from "@components/VersionHistory";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import PreferredDefaultsFields from "@components/PreferredDefaultsFields";

interface Props {
  user: User;
  setUser: (user: User) => void;
  teams: V1TeamResource[];
  roles: V1RoleResource[];
  account: V1AccountResource;
  isArchived?: boolean;
  editor?: Editor;
  onTabChange?: (key: string) => void;
}
const UserInfo = ({
  user,
  setUser,
  teams,
  roles,
  account,
  isArchived,
  editor,
  onTabChange,
}: Props) => {
  const isCreate = window.location.href.includes("create");
  const [showServiceConfirm, setShowServiceConfirm] = useState(false);
  const labels = account?.attributes?.["field-labels"];
  const subheading = {
    color: "#1c2655",
    padding: "8px 0px",
    fontWeight: 700,
    marginBottom: "10px",
  };

  const { privileges, isLoading } = useListPrivileges();

  const formatPrivilege = (str) => {
    return str
      .split(/[._]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const privilegeColumns: ColumnDef<Privilege>[] = [
    {
      header: "Resource",
      cell(props) {
        const id = props.row.original.id;
        return id === "none"
          ? "No privileges found"
          : formatPrivilege(props.row.original.id);
      },
    },
    {
      header: "Description",
      cell(props) {
        return props.row.original.description;
      },
    },
    {
      header: "Privilege",
      cell(props) {
        const privilege = props.row.original;
        const privilegeLevel =
          user?.privileges?.[privilege.id] || privilege?.levels?.[0] || "";
        return privilege.id === "none" ? "" : snakeToText(privilegeLevel);
      },
    },
  ];

  const startPageOptions = [
    "Home",
    "Projects",
    "Approvals",
    "Surveys",
    "Settings",
  ].map((opt) => {
    return { label: opt, value: opt };
  });
  const userInfoContent = (
    <>
      <Card.Title style={subheading}>User Contact</Card.Title>
      <Row>
        {(["name", "email"] as const).map((key) => (
          <Col key={key}>
            <TextField
              id={key}
              label={"*" + capitalizeFirstLetter(key)}
              value={user[key] || ""}
              placeholder={`Enter ${key}`}
              onChange={(e) => setUser({ ...user, [key]: e.target.value })}
              readOnly={isArchived}
            />
          </Col>
        ))}
      </Row>
      <Row>
        {(["phone", "title"] as const).map((key) => (
          <Col key={key}>
            <TextField
              id={key}
              label={capitalizeFirstLetter(key)}
              value={user[key] || ""}
              placeholder={`Enter ${key}`}
              onChange={(e) => setUser({ ...user, [key]: e.target.value })}
              readOnly={isArchived}
            />
          </Col>
        ))}
      </Row>
      <Row>
        <Col>
          <PreferredDefaultsFields
            rateTableId={user.rateTableId ?? null}
            currencyId={user.currencyId ?? null}
            currentRateTable={user.currentRateTable}
            currentCurrency={user.currentCurrency}
            onRateTableChange={(id) => setUser({ ...user, rateTableId: id })}
            onCurrencyChange={(id) => setUser({ ...user, currencyId: id })}
          />
        </Col>
      </Row>
      {editor?.attributes?.siteAdmin && (
        <Row>
          <Col key={"startPage"}>
            <Select2V2
              label="Start Page"
              value={startPageOptions?.find(
                (opt) => opt.value === user.startPage
              )}
              options={startPageOptions}
              onChange={(e) =>
                setUser({
                  ...user,
                  startPage: e.value,
                })
              }
            />
          </Col>
          <Col key={"provider"}>
            <TextField
              id={"provider"}
              label={"Custom SSO Provider"}
              value={user.ssoProvider || ""}
              onChange={(e) =>
                setUser({ ...user, ["ssoProvider"]: e.target.value })
              }
              readOnly={isArchived}
            />
          </Col>
        </Row>
      )}
      <hr />
      <Card.Title style={subheading}>User Type, Teams, and Roles</Card.Title>
      <Row>
        {[
          {
            value: "licensed",
            description:
              "A paid user with full edit access (subject to role assignments)",
          },
          {
            value: "sales",
            description:
              "A paid user with limited ability to check project status and use the Sales app",
          },
          {
            value: "service",
            description:
              "A free service account may access the API but not use the application",
          },
          {
            value: "view_only",
            description:
              "A paid user with limited ability to review projects and approve.",
          },
        ].map((type) => {
          const selected = user.userType === type.value;
          return (
            <Col key={type.value}>
              <div className={`userType ${selected ? "selected" : ""}`}>
                <Form.Check
                  type="radio"
                  className="typeCheckbox"
                  disabled={isArchived}
                  accessKey={type.value}
                  label={
                    <div
                      style={{
                        color: selected ? "#418172" : "#1C2655",
                      }}
                    >
                      <strong>{snakeToText(type.value)}</strong>
                      <div>{type.description}</div>
                    </div>
                  }
                  checked={selected}
                  onChange={(e) => {
                    const userType = e.target.accessKey;
                    // Service accounts can't sign in, so confirm before switching;
                    // the current type stays selected until they do.
                    if (userType === "service") {
                      setShowServiceConfirm(true);
                    } else {
                      setUser({ ...user, userType });
                    }
                  }}
                />
              </div>
            </Col>
          );
        })}
      </Row>
      <div className="displayFlex">
        {[
          {
            value: "sales",
            label: labels?.sales_executive || "Sales Executive",
          },
          {
            value: "presales",
            label: labels?.presales_engineer || "Pre-Sales Engineer",
          },
        ].map((option, i) => {
          const checked = user?.[option.value];
          return (
            <div key={i} className={`roleOption ${checked ? "active" : ""}`}>
              <FormCheck
                required={false}
                id={option.value}
                inline
                disabled={isArchived}
                key={option.value}
                checked={checked}
                onChange={(e) =>
                  setUser({ ...user, [option.value]: e.target.checked })
                }
              />{" "}
              Show in list of {option.label}s
            </div>
          );
        })}
      </div>

      <Row>
        <Col md={6}>
          <MultiSelectAutocomplete
            label="Teams"
            options={teams.map((t) => {
              return { label: t.attributes.name, value: String(t.id) };
            })}
            value={user.teams}
            //@ts-ignore
            onChange={(e) => setUser({ ...user, teams: e })}
            isDisabled={isArchived}
          />
        </Col>
        <Col md={6}>
          <MultiSelectAutocomplete
            label="*Roles"
            options={roles.map((r) => {
              return { label: r?.attributes?.name || "", value: String(r.id) };
            })}
            value={user.roles}
            //@ts-ignore
            onChange={(e) => setUser({ ...user, roles: e })}
            isDisabled={isArchived}
          />
        </Col>
      </Row>
    </>
  );

  const privilegesContent = isLoading ? (
    <SmallSpinner />
  ) : (
    <DataTable
      data={
        privileges?.length
          ? privileges
          : [{ id: "none", description: "", levels: [] }]
      }
      columns={privilegeColumns}
      striped
      bordered
      hover
    />
  );

  const { releaseServiceAccountCredentials } = useFlags();
  const { hasPrivilege } = usePrivilegeCheck();
  const isServiceAccount = user.userType === "service";
  const canManageAccount = hasPrivilege("settings.account", ["manage"]);
  const canManageUsers = hasPrivilege("settings.users", ["manage"]);
  // `account` is the raw useGetAccountQuery response, so the resource lives under `data`
  const accountResource = (account as any)?.data ?? account;

  return (
    <Tab.Container
      defaultActiveKey="userInfo"
      onSelect={(key) => key && onTabChange?.(key)}
    >
      <div className="tabsHeaderRow">
        <Nav variant="tabs" className="nav-tabs">
          <Nav.Item>
            <Nav.Link eventKey="userInfo" className="userInfoTabNav">
              User Info
            </Nav.Link>
          </Nav.Item>
          {!isCreate && (
            <Nav.Item>
              <Nav.Link eventKey="privileges" className="userInfoTabNav">
                Privileges
              </Nav.Link>
            </Nav.Item>
          )}
          {!isCreate &&
            isServiceAccount &&
            canManageAccount &&
            releaseServiceAccountCredentials && (
              <Nav.Item>
                <Nav.Link
                  eventKey="clientCredentials"
                  className="userInfoTabNav"
                >
                  Client Credentials
                </Nav.Link>
              </Nav.Item>
            )}
          {!isCreate && canManageUsers && (
            <Nav.Item>
              <Nav.Link eventKey="versionHistory" className="userInfoTabNav">
                Version History
              </Nav.Link>
            </Nav.Item>
          )}
        </Nav>
      </div>
      <Tab.Content>
        <Tab.Pane eventKey="userInfo" className="userInfoTabContent">
          {userInfoContent}
        </Tab.Pane>
        {!isCreate && (
          <Tab.Pane eventKey="privileges" className="userInfoTabContent">
            {privilegesContent}
          </Tab.Pane>
        )}
        {!isCreate &&
          isServiceAccount &&
          canManageAccount &&
          releaseServiceAccountCredentials && (
            <Tab.Pane
              eventKey="clientCredentials"
              className="userInfoTabContent"
            >
              <ClientCredentials userId={String(user.id)} />
            </Tab.Pane>
          )}
        {!isCreate && canManageUsers && (
          <Tab.Pane eventKey="versionHistory" className="userInfoTabContent">
            <VersionHistory
              filter={{
                "context-type": "User",
                "context-id": String(user.id),
              }}
              permission={canManageUsers}
              account={accountResource}
            />
          </Tab.Pane>
        )}
      </Tab.Content>
      <ScopeStackModal
        show={showServiceConfirm}
        modalTitle="Change to Service Account"
        modalBody={
          <>
            <p>
              A service account can access the ScopeStack API but cannot log in
              to the pages of the application.
            </p>
            <p>Do you want to continue?</p>
          </>
        }
        handleClose={() => setShowServiceConfirm(false)}
        button1Text="Cancel"
        handleButton1Click={() => setShowServiceConfirm(false)}
        button2Text="Continue"
        handleButton2Click={() => {
          setUser({ ...user, userType: "service" });
          setShowServiceConfirm(false);
        }}
      />
    </Tab.Container>
  );
};

export default UserInfo;
